import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { DocumentArrowDownIcon, PlayIcon } from '@heroicons/react/24/outline';
import { Card, CardHeader } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { useAuth } from '../context/AuthContext';
import { payrollService } from '../services/payrollService';
import { PayrollRecord, PayrollSummary } from '../types';

const monthOptions = [
  { value: '1', label: 'Jan' },
  { value: '2', label: 'Feb' },
  { value: '3', label: 'Mar' },
  { value: '4', label: 'Apr' },
  { value: '5', label: 'May' },
  { value: '6', label: 'Jun' },
  { value: '7', label: 'Jul' },
  { value: '8', label: 'Aug' },
  { value: '9', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
];

export function PayrollPage() {
  const { isHR } = useAuth();
  const [myRecords, setMyRecords] = useState<PayrollRecord[]>([]);
  const [allRecords, setAllRecords] = useState<PayrollRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState<PayrollSummary | null>(null);

  useEffect(() => {
    fetchPayrollData();
  }, [selectedMonth, selectedYear]);

  const fetchPayrollData = async () => {
    setIsLoading(true);
    try {
      const records = await payrollService.getMyPayrollRecords();
      setMyRecords(records);

      if (isHR) {
        const all = await payrollService.getPayrollRecords(selectedMonth, selectedYear);
        setAllRecords(all);
      }
    } catch (error) {
      console.error('Error fetching payroll:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunPayroll = async () => {
    setIsRunning(true);
    try {
      const result = await payrollService.runPayroll({
        month: selectedMonth,
        year: selectedYear,
      });
      setSummary(result);
      toast.success('Payroll processed successfully!');
      fetchPayrollData();
    } catch (error) {
      // Error handled by interceptor
    } finally {
      setIsRunning(false);
    }
  };

  const handleDownloadPayslip = async (payrollId: number, month: number, year: number) => {
    try {
      const blob = await payrollService.downloadPayslip(payrollId);
      
      // Check if blob is valid
      if (!blob || blob.size === 0) {
        toast.error('Payslip file is empty or invalid');
        return;
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${month}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded successfully');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to download payslip';
      toast.error(errorMessage);
      console.error('Error downloading payslip:', error);
    }
  };

  const formatCurrency = (amount: number | string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll</h1>
          <p className="text-gray-500">View and manage payroll records</p>
        </div>
      </div>

      {/* HR: Run Payroll Section */}
      {isHR && (
        <Card>
          <CardHeader title="Run Monthly Payroll" />
          <div className="flex items-end gap-4">
            <div className="w-40">
              <Select
                label="Month"
                value={String(selectedMonth)}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                options={monthOptions}
              />
            </div>
            <div className="w-40">
              <label className="label">Year</label>
              <Input
                type="number"
                min={2020}
                max={2030}
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              />
            </div>
            <Button
              onClick={handleRunPayroll}
              isLoading={isRunning}
              leftIcon={<PlayIcon className="w-5 h-5" />}
            >
              Run Payroll
            </Button>
          </div>
          
          {summary && (
            <div className="mt-4 p-4 bg-success-50 rounded-lg">
              <h4 className="font-semibold text-success-700 mb-2">Payroll Summary</h4>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Employees</p>
                  <p className="font-bold text-lg">{summary.totalEmployees}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Gross</p>
                  <p className="font-bold text-lg">{formatCurrency(summary.totalGrossSalary)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Deductions</p>
                  <p className="font-bold text-lg text-danger-600">{formatCurrency(summary.totalDeductions)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Net</p>
                  <p className="font-bold text-lg text-success-600">{formatCurrency(summary.totalNetSalary)}</p>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* My Payroll Records */}
      <Card>
        <CardHeader title="My Payroll History" />
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="spinner" />
          </div>
        ) : myRecords.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No payroll records found.
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Working Days</th>
                  <th>Present</th>
                  <th>Leave</th>
                  <th>Gross</th>
                  <th>Deductions</th>
                  <th>Net Salary</th>
                  <th>Payslip</th>
                </tr>
              </thead>
              <tbody>
                {myRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="font-medium">
                      {new Date(record.year, record.month - 1).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td>{record.workingDays}</td>
                    <td className="text-success-600">{record.presentDays}</td>
                    <td className="text-warning-600">{record.leaveDays}</td>
                    <td>{formatCurrency(record.grossSalary)}</td>
                    <td className="text-danger-600">-{formatCurrency(record.totalDeductions)}</td>
                    <td className="font-bold text-success-600">{formatCurrency(record.netSalary)}</td>
                    <td>
                      {record.payslipPath && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPayslip(record.id, record.month, record.year)}
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* HR: All Payroll Records */}
      {isHR && (
        <Card>
          <CardHeader
            title={`All Payroll Records - ${new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
          />
          {allRecords.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No payroll records for this period. Run payroll to generate.
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Base Salary</th>
                    <th>Present</th>
                    <th>Unpaid</th>
                    <th>Deductions</th>
                    <th>Net Salary</th>
                    <th>Payslip</th>
                  </tr>
                </thead>
                <tbody>
                  {allRecords.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <div>
                          <p className="font-medium">
                            {record.employee?.firstName} {record.employee?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{record.employee?.employeeCode}</p>
                        </div>
                      </td>
                      <td>{record.employee?.department || '-'}</td>
                      <td>{formatCurrency(record.baseSalary)}</td>
                      <td className="text-success-600">{record.presentDays}</td>
                      <td className="text-danger-600">{record.unpaidLeaveDays}</td>
                      <td className="text-danger-600">-{formatCurrency(record.totalDeductions)}</td>
                      <td className="font-bold">{formatCurrency(record.netSalary)}</td>
                      <td>
                        {record.payslipPath && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadPayslip(record.id, record.month, record.year)}
                          >
                            <DocumentArrowDownIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
